import { yupResolver } from "@hookform/resolvers/yup";
import { iPackageSize } from "core/kangarooTypes/iPackageSize";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import Button from "shared/Button";
import { Dialog } from "shared/Dialog/Dialog";
import { DialogContent } from "shared/Dialog/DialogContent";
import { DialogFooter } from "shared/Dialog/DialogFooter";
import { DialogHeader } from "shared/Dialog/DialogHeader";
import { DialogTitle } from "shared/Dialog/DialogTitle";
import FormInput from "shared/FormInput"; 
import * as yup from "yup";
import { useTranslation } from "react-i18next";

export type PackageFormData = Omit<iPackageSize, "id">;

interface PackageFormDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: PackageFormData) => void;
  initialData?: iPackageSize;
  isEditing: boolean;
}

const PackageFormDialog = ({
  isOpen,
  onClose,
  onSubmit,
  initialData,
  isEditing,
}: PackageFormDialogProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation();
  
  // Form validation schema for packages
  const PackageFormSchema = yup.object({
    name: yup.string().required(t("packageFormDialog.validation.nameRequired")),
    description: yup.string().required(t("packageFormDialog.validation.descriptionRequired")),
    price: yup
      .number()
      .required(t("packageFormDialog.validation.priceRequired"))
      .min(0, t("packageFormDialog.validation.pricePositive")),
  });
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<PackageFormData>({
    resolver: yupResolver(PackageFormSchema),
    defaultValues: initialData || {
      name: "",
      description: "",
      price: 0,
    },
  });

  useEffect(() => {
    if (isOpen) {
      reset(
        initialData || {
          id: 0,
          name: "",
          description: "",
          price: 0,
        }
      );
    }
  }, [initialData, reset, isOpen]);

  const handleFormSubmit = async (data: PackageFormData) => {
    try {
      setIsSubmitting(true);
      await onSubmit(data);
      handleClose();
    } catch (error) {
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    onClose();
    reset({
      name: "",
      description: "",
      price: 0,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex justify-between items-center">
            <span>
              {isEditing 
                ? t("packageFormDialog.title.edit")
                : t("packageFormDialog.title.add")}
            </span>
            <button
              onClick={handleClose}
              className="rounded-full p-1 hover:bg-gray-100 transition-colors"
            >
              <X size={18} />
            </button>
          </DialogTitle>
        </DialogHeader>

        <div className="p-6 flex flex-col h-full justify-start">
          <div className="grid grid-cols-1  gap-4">
            <FormInput
              label={t("packageFormDialog.form.packageName")}
              name="name"
              register={register}
              errors={errors}
              placeholder={t("packageFormDialog.form.packageNamePlaceholder")}
            />
            <FormInput
              label={t("packageFormDialog.form.description")}
              name="description"
              type="textarea"
              register={register}
              errors={errors}
              placeholder={t("packageFormDialog.form.descriptionPlaceholder")}
            />
            <FormInput
              label={t("packageFormDialog.form.price")}
              name="price"
              type="number"
              register={register}
              errors={errors}
              placeholder={t("packageFormDialog.form.pricePlaceholder")}
            />
          </div>
        </div>

        <DialogFooter className="mt-4 sm:mt-6 flex flex-col sm:flex-row gap-2">
          <Button
            type="button"
            variant="outlined"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            {t("packageFormDialog.buttons.cancel")}
          </Button>
          <Button
            onClick={handleSubmit(handleFormSubmit)}
            isLoading={isSubmitting}
          >
            {isEditing 
              ? t("packageFormDialog.buttons.update")
              : t("packageFormDialog.buttons.add")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
export default PackageFormDialog;
